import { Immutable } from "immer";
import React, { useState } from "react";

import type { ProductTable as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import leadInStyles from "../../styles/composable/lead-in.module.scss";
import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

type ColumnLabel = NonNullable<Value["column_labels"]>[number];
type Row = NonNullable<Value["rows"]>[number];
type CTACell = NonNullable<Value["cta"]>[number];
type CTAButton = NonNullable<CTACell["buttons"]>[number];

// Column badge ("Good" / "Better" / "Best") by column index; later columns
// have none.
const HEAD_CLASSES = [
    styles.prodchartCellHead1,
    styles.prodchartCellHead2,
    styles.prodchartCellHead3,
];

/**
 * React port of the sealy ``ProductTable`` block (legacy template
 * ``components/product_tables/product_table.html`` plus the legacy
 * module-eval script ``client/src/ui/product-table.ts``).
 *
 * Interactivity notes (matching legacy *production* behavior):
 *
 * - Tabs: the legacy script added ``prodchart__cell--on`` to every
 *   ``[data-column="0"]`` cell at init time and moved that class (plus the
 *   ``aria-selected`` flip) between columns on tab click. ``--on`` is the
 *   mobile visibility mechanism (see ``_prodchart.scss``: at small
 *   breakpoints cells are ``display: none`` unless ``--on``). We bake the
 *   initial column-0 state into the render and own tab clicks with React
 *   state, scoped per table instance (the legacy script's document-global
 *   selectors leaked across multiple tables; per-instance scoping is a
 *   correctness fix, not a behavior change for the single-table case).
 *
 * - Collapsible rows: the legacy ``.prodchart-trigger`` handler has been a
 *   no-op since the 2020 jQuery removal (#16033, commit a4d29c8b2c): the
 *   jQuery ``$(this).data("id-trigger")`` lookup was rewritten as
 *   ``elem.dataset["id-trigger"]``, but DOM ``dataset`` camelCases keys
 *   (``data-id-trigger`` -> ``dataset.idTrigger``), so the lookup returns
 *   ``undefined`` and the ``[data-extra-id="undefined"]`` query matches
 *   nothing. Production cells therefore stay ``prodchart__cell--closed``
 *   forever. We intentionally reproduce that steady state: the trigger
 *   anchor is rendered for visual parity but implementing a working toggle
 *   would CHANGE long-standing production behavior.
 *
 * The Django ``|cut:" "`` filter (used for ``data-extra-id`` /
 * ``data-id-trigger``) removes every space character; ``stripSpaces``
 * replicates it exactly.
 */
const stripSpaces = (text: string | null | undefined): string =>
    (text ?? "").split(" ").join("");

/**
 * Django ``LinkValue.get_url`` parity: a chosen page wins (``page.url or
 * ""``); otherwise fall back to ``external_url`` (or ``""``).
 */
const getLinkURL = (link: Immutable<CTAButton>["link"]): string => {
    if (link?.page) {
        return link.page.url || "";
    }
    return link?.external_url ?? "";
};

const ColumnHeadCell = (
    props: Immutable<{
        label: ColumnLabel;
        index: number;
        isOn: boolean;
        posturepedicLogoURL: string;
    }>,
): React.JSX.Element => {
    // Legacy: ``{% image column_label.image original as image %}`` then
    // ``image.url`` -- prefer the serialized ``original`` rendition.
    const imageURL =
        props.label.image?.renditions["original"]?.url ??
        props.label.image?.url ??
        "";
    return (
        <div
            data-column={props.index}
            className={concatClassNames([
                styles.prodchartCell,
                styles.prodchartCellHead,
                HEAD_CLASSES[props.index],
                props.isOn ? styles.prodchartCellOn : null,
            ])}
        >
            <div className={styles.prodchartCellHeadContent}>
                <span className={styles.prodchartCellImage}>
                    <img
                        src={imageURL}
                        alt={`${props.label.title ?? ""} Bed`}
                        loading="lazy"
                        decoding="async"
                    />
                </span>
                <h5>{props.label.title}</h5>
                {props.label.logo && (
                    <span className={styles.prodchartCellBadge}>
                        {/* The "Posturpedic" alt typo is preserved from the
                            legacy template for parity. */}
                        <img
                            src={props.posturepedicLogoURL}
                            alt="Posturpedic Technology"
                            aria-hidden="true"
                            loading="lazy"
                            decoding="async"
                        />
                    </span>
                )}
                <p>{props.label.copy}</p>
            </div>
        </div>
    );
};

const RowCells = (
    props: Immutable<{
        row: Row;
        presentation: Value["presentation"];
        activeColumn: number;
    }>,
): React.JSX.Element => {
    const extraID = stripSpaces(props.row.title);
    const cells = props.row.cells ?? [];
    return (
        <>
            {props.row.collapsible ? (
                <div
                    data-extra-id={extraID}
                    className={concatClassNames([
                        styles.prodchartCell,
                        styles.prodchartCellDef,
                    ])}
                >
                    <p>{props.row.title}</p>
                    {/* The trigger is intentionally inert -- see the
                        collapsible-rows note in the component docstring. The
                        legacy href was ``javascript:void(0)``, but React 19
                        unconditionally rewrites ``javascript:`` URLs to a
                        throwing placeholder, so an inert ``#`` +
                        ``preventDefault`` reproduces the production no-op
                        instead. */}
                    <a
                        href="#"
                        className={styles.prodchartTrigger}
                        data-id-trigger={extraID}
                        title={`Toggle ${props.row.title ?? ""} Details`}
                        onClick={(event) => {
                            event.preventDefault();
                        }}
                    >
                        <figure className={styles.prodchartIcon}>
                            <svg className="icon-chevron">
                                <use xlinkHref="#icon-chevron" />
                            </svg>
                        </figure>
                    </a>
                    <p className={styles.prodchartExtra}>{props.row.copy}</p>
                </div>
            ) : (
                <div
                    className={concatClassNames([
                        styles.prodchartCell,
                        props.presentation === "mattress_base"
                            ? styles.prodchartCellDef
                            : null,
                    ])}
                >
                    {props.row.title}
                </div>
            )}
            {cells.map((cell, cellIndex) => {
                const cellClass = concatClassNames([
                    styles.prodchartCell,
                    cellIndex === props.activeColumn
                        ? styles.prodchartCellOn
                        : null,
                ]);
                const features = cell.features ?? [];
                return props.row.collapsible ? (
                    <div
                        key={cellIndex}
                        data-column={cellIndex}
                        data-extra-id={extraID}
                        className={cellClass}
                    >
                        {features.map((feature, featureIndex) => (
                            <React.Fragment key={featureIndex}>
                                <p>{feature.title}</p>
                                <p className={styles.prodchartExtra}>
                                    {feature.copy}
                                </p>
                            </React.Fragment>
                        ))}
                    </div>
                ) : (
                    <div
                        key={cellIndex}
                        data-column={cellIndex}
                        className={cellClass}
                    >
                        {/* Legacy Django rendered each title on its own
                            indented line, which HTML whitespace collapsing
                            displayed as a single space between titles. */}
                        {features.map((feature) => feature.title).join(" ")}
                    </div>
                );
            })}
        </>
    );
};

const CTAButtonLink = (
    props: Immutable<{ button: CTAButton }>,
): React.JSX.Element => {
    const link = props.button.link;
    return (
        <>
            {props.button.connector && <p>{props.button.connector}</p>}
            {/* ``rel="noreferrer"`` is a deviation from the legacy template
                (which set no rel); it is this repo's lint-enforced
                convention for target="_blank" links. */}
            <a
                className="button"
                target={link?.open_in_new_tab ? "_blank" : undefined}
                rel={link?.open_in_new_tab ? "noreferrer" : undefined}
                href={getLinkURL(link)}
                title={link?.html_title ?? ""}
            >
                {link?.display_name}
            </a>
        </>
    );
};

export const SealyProductTable = (props: Props): React.JSX.Element => {
    const value = props.value;
    const columnLabels = value.column_labels ?? [];
    const rows = value.rows ?? [];
    const cta = value.cta ?? [];
    // Legacy script's post-init steady state: column 0 is active.
    const [activeColumn, setActiveColumn] = useState(0);

    return (
        <section className="ptb">
            {/* title */}
            <div
                className={concatClassNames([leadInStyles.leadIn, "container"])}
            >
                <h2 className="lead-in__headline">{value.title}</h2>
                <p className={leadInStyles.leadInCopy}>{value.copy}</p>
            </div>

            <div
                className={concatClassNames([
                    styles.prodchart,
                    styles.prodchartMarginTop,
                    styles.prodchart4cols,
                    styles.prodchartCollapse,
                    "js-prodchartTabs",
                ])}
            >
                {/* tab buttons for mobile */}
                <div className={styles.tablist} role="tablist">
                    <button
                        className={concatClassNames([
                            styles.tab,
                            styles.hiddenSmall,
                        ])}
                        role="tab"
                        aria-selected="false"
                    >
                        empty
                    </button>
                    {columnLabels.map((label, index) => (
                        <button
                            key={index}
                            className={styles.tab}
                            role="tab"
                            aria-selected={
                                index === activeColumn ? "true" : "false"
                            }
                            onClick={() => {
                                setActiveColumn(index);
                            }}
                        >
                            {label.subtitle}
                        </button>
                    ))}
                </div>

                {/* 1st row */}
                <div
                    className={concatClassNames([
                        styles.prodchartCell,
                        styles.prodchartCellHidden,
                    ])}
                >
                    <h3>EMPTY</h3>
                </div>
                {columnLabels.map((label, index) => (
                    <ColumnHeadCell
                        key={index}
                        label={label}
                        index={index}
                        isOn={index === activeColumn}
                        posturepedicLogoURL={value.posturepedic_logo_url}
                    />
                ))}

                {/* rows */}
                {rows.map((row, rowIndex) => (
                    <RowCells
                        key={rowIndex}
                        row={row}
                        presentation={value.presentation}
                        activeColumn={activeColumn}
                    />
                ))}

                {/* last row */}
                <div
                    className={concatClassNames([
                        styles.prodchartCell,
                        styles.prodchartCellHidden,
                    ])}
                >
                    empty
                </div>
                {cta.map((ctaCell, index) => (
                    <div
                        key={index}
                        data-column={index}
                        className={concatClassNames([
                            styles.prodchartCell,
                            styles.prodchartCellCta,
                            index === activeColumn
                                ? styles.prodchartCellOn
                                : null,
                        ])}
                    >
                        {(ctaCell.buttons ?? []).map((button, buttonIndex) => (
                            <CTAButtonLink key={buttonIndex} button={button} />
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
};
